import React, { createContext, useContext, useState } from 'react';

interface CollaborationEmail {
  id: string;
  campaign_id: string;
  sequence_order: number;
  subject: string;
  content: string;
  delay_days: number;
  delay_hours: number;
  send_conditions: {
    stop_if_replied: boolean;
    stop_if_opened: boolean;
  };
}

interface CollaborationEmailContextType {
  isOpen: boolean;
  editingEmail: CollaborationEmail | null;
  campaignId: string | null;
  openAddEmail: (campaignId: string) => void;
  openEditEmail: (email: CollaborationEmail) => void;
  closeEmailDialog: () => void;
}

const CollaborationEmailContext = createContext<CollaborationEmailContextType | undefined>(undefined);

export const CollaborationEmailProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState<CollaborationEmail | null>(null);
  const [campaignId, setCampaignId] = useState<string | null>(null);

  const openAddEmail = (campaignId: string) => {
    setCampaignId(campaignId);
    setEditingEmail(null);
    setIsOpen(true);
  };

  const openEditEmail = (email: CollaborationEmail) => {
    setCampaignId(email.campaign_id);
    setEditingEmail(email);
    setIsOpen(true);
  };

  const closeEmailDialog = () => {
    setIsOpen(false);
    setEditingEmail(null);
    setCampaignId(null);
  };

  return (
    <CollaborationEmailContext.Provider 
      value={{ 
        isOpen, 
        editingEmail,
        campaignId,
        openAddEmail, 
        openEditEmail, 
        closeEmailDialog 
      }}
    >
      {children}
    </CollaborationEmailContext.Provider>
  );
};

export const useCollaborationEmail = () => {
  const context = useContext(CollaborationEmailContext);
  if (context === undefined) {
    throw new Error('useCollaborationEmail must be used within a CollaborationEmailProvider');
  }
  return context;
};
